/**
 * src/api/serviceRestart.js
 *
 * A Function App restart (deploy, scale-out, host recycle) answers 502/503/504
 * from the platform front end BEFORE any function code runs, for seconds up to
 * a couple of minutes. Rather than surfacing that as a failed save in every
 * module, azureClient waits it out here: the call is retried with backoff, the
 * caller's promise stays pending (so its modal keeps the user's input and its
 * "saving" state), and one app-wide banner says what is happening.
 *
 * What is retried:
 *   - GET/reads: 502, 503, 504.
 *   - Writes (POST): 503 only. A front end that says "unavailable" never
 *     handed the request to the function host. A 502 or 504 means
 *     the request may have partly executed, so a blind replay could double-apply it —
 *     it is surfaced like any other failure.
 *
 * ...EXCEPT a 503 the function host returned ITSELF. That premise above — "a
 * front end that says unavailable never handed the request to the function
 * host" — is true of the Azure platform front end and false of an application
 * refusal, and the status code cannot tell them apart. 25 responses under
 * azure-functions/** answer 503 deliberately, 10 of them on write endpoints,
 * and every one of them was being read here as a restart: replayed blindly,
 * and reported to the user as a change that is kept. See isApplicationRefusal.
 *
 * The wait is bounded (RESTART_MAX_WAIT_MS); past it the original error is
 * thrown and every existing failure path behaves exactly as before.
 * Nothing is persisted: closing the tab mid-restart loses a queued write.
 */

export const RESTART_MAX_WAIT_MS = 120_000;
const BACKOFF_MS = [2000, 3000, 5000, 5000, 8000];

export const RESTART_EVENT = 'seasuite:service-restarting';

const READ_STATUSES = new Set([502, 503, 504]);
const WRITE_STATUSES = new Set([503]);

export function isRestartStatus(status, { write = false } = {}) {
  return (write ? WRITE_STATUSES : READ_STATUSES).has(status);
}

/**
 * Did the function host answer this itself, or did the platform answer for it?
 *
 * The discriminator is the BODY, because the status code cannot carry the
 * difference. Every application response in this codebase goes through
 * `shared/cors.js`'s `jsonResponse`, which always sets
 * `Content-Type: application/json` and JSON-stringifies a body with a string
 * `error`. A platform front-end 503 is generated before any function code runs
 * and carries no such body.
 *
 * What this stops, measured on development 2026-10-10 (cycle br10-01):
 * `incidents-write` answered `503 {"error":"The incident could not be
 * classified just now — nothing was saved. Try again.","reason":
 * "classification-unavailable"}`, and the product replayed it and showed
 * "Service is restarting — your changes are kept and will save automatically"
 * over a server that had said nothing was saved. An administrator adding a
 * response action to a personal-data breach was left believing it was recorded.
 *
 * FAILS SAFE TOWARD THE TRUTH, deliberately. An unreadable or unparseable body
 * is treated as the platform — i.e. retried exactly as before — so the only way
 * to reach the "refusal" branch is to have actually read a refusal. Weigh the
 * two mistakes: calling a platform 503 a refusal tells the user "not saved" for
 * a write that might have succeeded on retry, and costs a click; calling a
 * refusal a restart tells the user their change is kept over a server that said
 * it was not. Only one of those is a false claim about stored data.
 *
 * THE GAP THIS USED TO NAME IS CLOSED (GH #7673). It read: `health` answers 503
 * with `{ status: 'degraded', at }` — no `error`, no `reason` — so this will not
 * catch it, and the real contract is for the server to declare retryability.
 * That contract now exists, and it is better than `error`/`reason`:
 * `shared/errorCapture.js` adds a `correlationId` to the body of EVERY 5xx the
 * function host produces (338 wrapped functions), and it is the sole
 * post-handler mutator of a response body, so nothing else can forge one. A
 * platform front-end 5xx is generated before any function code runs and
 * therefore can never carry one.
 *
 * So `correlationId` is an ADDITIONAL disjunct, never a required one — an
 * unwrapped function's 503 still has to be recognised by its `error`. The
 * direction is safe either way: a new true-condition can only move a
 * classification from "retry" to "surface", which is the mistake this file
 * already argues is the safe one.
 *
 * @param {Response} res
 * @returns {Promise<boolean>}
 */
async function isApplicationRefusal(res) {
  const type = res?.headers?.get?.('content-type') || '';
  if (!/json/i.test(type)) return false;
  try {
    // clone() so the caller still reads the body normally — azureClient's own
    // response.clone().json() for adoptTenantStateVersion must be unaffected.
    const body = await res.clone().json();
    return !!(
      body &&
      typeof body === 'object' &&
      (typeof body.error === 'string' ||
        typeof body.reason === 'string' ||
        // The function host's own fingerprint — see the note above. Present on
        // every 5xx it produces and impossible on a platform front-end one.
        typeof body.correlationId === 'string')
    );
  } catch {
    return false;
  }
}

let _waiting = 0;

function announce(active) {
  try {
    window.dispatchEvent(new CustomEvent(RESTART_EVENT, { detail: { active } }));
  } catch {
    /* non-browser environment — no-op */
  }
}

/**
 * Run `doFetch` (returns a Response); while it answers a restart status, wait
 * and run it again, up to RESTART_MAX_WAIT_MS. Returns the last Response — the
 * caller's normal !res.ok handling then applies if the wait ran out.
 *
 * @param {() => Promise<Response>} doFetch
 * @param {{ write?: boolean, sleep?: (ms: number) => Promise<void> }} [opts]
 */
export async function fetchThroughRestart(doFetch, { write = false, sleep } = {}) {
  const wait = sleep || ((ms) => new Promise((r) => setTimeout(r, ms)));
  let res = await doFetch();
  // The status check first, so a normal response is never cloned or parsed —
  // the body read below costs nothing on the 99% path, and keeping it after
  // this early return also keeps it clear of azureClient's own clone().json().
  if (!isRestartStatus(res.status, { write })) return res;
  if (await isApplicationRefusal(res)) return res;

  let waited = 0;
  let attempt = 0;
  _waiting += 1;
  announce(true);
  try {
    while (isRestartStatus(res.status, { write }) && waited < RESTART_MAX_WAIT_MS) {
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      await wait(delay);
      waited += delay;
      attempt += 1;
      res = await doFetch();
      // A wait can turn into a refusal partway through: the host comes back and
      // then declines the write on its own terms. Stop replaying the moment it
      // does, rather than spending the rest of the budget on an answer that
      // will not change.
      //
      // The status check guards the body read, and that ordering matters on the
      // recovery path specifically: this loop exits on a 200, and a recovered
      // `state-read` 200 carries Content-Type: application/json over the
      // largest body in the product. Without the guard its whole tenant-state
      // blob is cloned and parsed here, thrown away, and parsed again by the
      // caller — after every deploy.
      if (isRestartStatus(res.status, { write }) && (await isApplicationRefusal(res))) break;
    }
  } finally {
    _waiting -= 1;
    if (_waiting === 0) announce(false);
  }
  return res;
}
